import React from 'react';
import { Animated, TouchableOpacity } from 'react-native';

import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { useHover } from '@hooks/useHover';

import type { Theme } from '@themes/theme';

interface FilterSectionHeaderProps {
  title: string;
  badgeNumber?: number;
  onPress: () => void;
  iconAnim?: Animated.Value;
  isExpanded?: boolean;
  accessibilityLabel?: string;
  showTopBorder?: boolean;
  height?: number;
  wrapEntireRow?: boolean;
  px?: keyof Theme['spacing'];
}

/**
 * Shared collapsible section header for filter panels.
 * Supports two modes:
 * - Animated: uses Animated.Value for smooth chevron rotation (0→180deg)
 * - Static: uses isExpanded boolean to swap ChevronDown/ChevronUp
 */
export const FilterSectionHeader = ({
  title,
  badgeNumber,
  onPress,
  iconAnim,
  isExpanded,
  px = 'm',
  accessibilityLabel,
  height = 50,
  wrapEntireRow = true,
  showTopBorder = true,
}: FilterSectionHeaderProps) => {
  // Section-header rule (TA-9005): structural row — full width tints to grey01
  // on hover (not greenSecondaryMild). Web-only; no-op on native.
  const { isHovered, hoverProps } = useHover();
  const chevron = iconAnim ? (
    <Animated.View
      style={{
        transform: [
          {
            rotate: iconAnim.interpolate({
              inputRange: [0, 1],
              outputRange: ['0deg', '180deg'],
            }),
          },
        ],
      }}>
      <Icon variant='l' name='ChevronDown' color='grey05' />
    </Animated.View>
  ) : (
    <Icon
      height={24}
      width={24}
      name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
      color='textPrimary'
    />
  );

  const badge =
    badgeNumber && badgeNumber > 0 ? (
      <Box
        mx='xs'
        minWidth={20}
        height={20}
        alignItems='center'
        justifyContent='center'
        backgroundColor='greenSecondary'
        borderRadius='s'>
        <Text variant='webBodyHintSubline' color='white'>
          {badgeNumber}
        </Text>
      </Box>
    ) : (
      <Box />
    );

  const content = (
    <Box
      {...(wrapEntireRow ? hoverProps : {})}
      backgroundColor={wrapEntireRow && isHovered ? 'grey01' : undefined}
      borderTopWidth={showTopBorder ? 1 : 0}
      borderTopColor='grey02'
      flexDirection='row'
      alignItems='center'
      height={height}
      px={px}>
      <Text
        flex={1}
        variant='labelEmphasized'
        color='textSecondary'
        numberOfLines={1}>
        {title}
      </Text>
      {badge}
      {wrapEntireRow ? (
        chevron
      ) : (
        <TouchableOpacity
          accessibilityLabel={accessibilityLabel || `${title} filter section`}
          accessibilityRole='button'
          onPress={onPress}>
          {chevron}
        </TouchableOpacity>
      )}
    </Box>
  );

  if (wrapEntireRow) {
    return (
      <TouchableOpacity
        accessibilityLabel={accessibilityLabel || `${title} filter section`}
        accessibilityRole='button'
        onPress={onPress}>
        {content}
      </TouchableOpacity>
    );
  }

  return content;
};
